import { createSelector } from '@reduxjs/toolkit';

import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import {
  allItemsReceivedSelector,
  lastPageNumberSelector,
  noItemsReceivedSelector,
  searchRequestSelector,
} from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import { getUriFromRequest } from 'Modules/converter/index';

import PaginationButton, { type PaginationButtonTemplateParams } from './paginationButton';
import { makeButtonProps } from './propsHelper';

const hidePaginationSelector = createSelector(
  noItemsReceivedSelector,
  allItemsReceivedSelector,
  (noItemsReceived, allItemsReceived) => noItemsReceived || allItemsReceived,
);

interface Props {
  template: TemplateFunction<PaginationTemplateParams>;
}

export interface PaginationButtonsTemplateParams {
  buttons: TemplateFunctionInvoker<PaginationButtonTemplateParams>[];
}

export type PaginationTemplateParams = PaginationButtonsTemplateParams;

export default function Pagination({ template }: Props): TemplateResult {
  const hidePagination = useSelector(hidePaginationSelector);
  const pageNumber = useSelector((state) => state.search.request.pageNumber);
  const lastPageNumber = useSelector(lastPageNumberSelector);
  const searchRequest = useSelector(searchRequestSelector);

  if (hidePagination) {
    return null;
  }

  const buttons = makeButtonProps(pageNumber, lastPageNumber).map(
    (buttonProps) => (templ: TemplateFunction<PaginationButtonTemplateParams>) => {
      const { pathname, search } = getUriFromRequest({
        ...searchRequest,
        pageNumber: buttonProps.pageNumber,
      });

      const props = {
        ...buttonProps,
        pageLink: `${pathname}${search}`,
        template: templ,
        isActive: buttonProps.pageNumber === pageNumber,
      };
      return (
        <PaginationButton {...props} key={`page-btn-${buttonProps.type}-${buttonProps.pageNumber ?? 0}`} />
      );
    },
  );

  return template.call({ buttons });
}
